import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import DraftReviewForm, { REVIEW_TEXT_DEFAULTS } from "./DraftReviewForm";
import ThankYouPage, { THANK_YOU_DEFAULTS } from "./ThankYouPage";

const SAMPLE = {
  customerFirstName: "Sarah",
  orderNumber: "10042",
  customerEmail: "sarah@example.com",
  // Two-item order to show the multi-image layout in preview
  images: [
    {
      imageUrl: "https://images.unsplash.com/photo-1574169208507-84376144848b?w=800&q=80",
      itemTitle: "Wachskerze mit Foto",
      personalization: "Foto: family_portrait.jpg\nText: \"Für immer in unseren Herzen\"\nDurchmesser: 7 cm",
    },
    {
      imageUrl: "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?w=800&q=80",
      itemTitle: "Geschenkkarte",
      personalization: "Empfänger: Oma Lieselotte\nText: \"Alles Liebe zum Geburtstag!\"",
    },
  ],
};

// ── Shared input/textarea styles ──────────────────────────────────────────────

const labelStyle = {
  display: "block", fontWeight: "600", fontSize: "0.8rem",
  color: "#4a5568", marginBottom: "0.3em",
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "0.55em 0.75em", border: "1px solid #cbd5e0",
  borderRadius: "6px", fontSize: "0.875rem",
  color: "#2d3748", background: "#fff",
  fontFamily: "inherit", marginBottom: "1.25em",
};
const taStyle = { ...inputStyle, resize: "vertical" };

// ── Component ─────────────────────────────────────────────────────────────────

const DraftReviewPreview = () => {
  const [subTab,       setSubTab]       = useState("review");
  // editMode: null | "review" | "thankyou"
  const [editMode,     setEditMode]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [reviewForm,   setReviewForm]   = useState(null);
  const [thankYouForm, setThankYouForm] = useState(null);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, "config", "reviewPage")),
      getDoc(doc(db, "config", "thankYouPage")),
    ]).then(([rSnap, tySnap]) => {
      setReviewForm(rSnap.exists()
        ? { ...REVIEW_TEXT_DEFAULTS, ...rSnap.data() }
        : { ...REVIEW_TEXT_DEFAULTS });
      setThankYouForm(tySnap.exists()
        ? { ...THANK_YOU_DEFAULTS, ...tySnap.data() }
        : { ...THANK_YOU_DEFAULTS });
    }).catch(() => {
      setReviewForm({ ...REVIEW_TEXT_DEFAULTS });
      setThankYouForm({ ...THANK_YOU_DEFAULTS });
    });
  }, []);

  const setReviewField   = (k, v) => setReviewForm((f) => ({ ...f, [k]: v }));
  const setThankYouField = (k, v) => setThankYouForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editMode === "review") {
        await setDoc(doc(db, "config", "reviewPage"), reviewForm);
      } else {
        await setDoc(doc(db, "config", "thankYouPage"), thankYouForm);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(null);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubTab = (tab) => { setSubTab(tab); setEditMode(null); };

  if (!reviewForm || !thankYouForm) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade Konfiguration…
      </p>
    );
  }

  // ── Sub-tab bar ────────────────────────────────────────────────────────────
  const subTabStyle = (key) => ({
    padding: "0.4em 1.1em", borderRadius: "6px",
    border: `1px solid ${subTab === key ? "#4a2c2a" : "#cbd5e0"}`,
    background: subTab === key ? "#4a2c2a" : "#fff",
    color: subTab === key ? "#fff" : "#4a5568",
    fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
  });

  const SubTabs = () => (
    <div style={{ display: "flex", gap: "0.5em", marginBottom: "0.85em" }}>
      <button style={subTabStyle("review")} onClick={() => handleSubTab("review")}>
        Review
      </button>
      <button style={subTabStyle("thankyou")} onClick={() => handleSubTab("thankyou")}>
        Danke-Seite
      </button>
    </div>
  );

  // ── Edit banner ────────────────────────────────────────────────────────────
  const EditBanner = () => (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: "#ebf8ff", border: "1px solid #90cdf4",
      borderRadius: "8px", padding: "0.75em 1.25em",
      marginBottom: "1.5em", gap: "1em",
    }}>
      <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
        ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-Seite für Kunden aus.
      </span>
      <button onClick={() => setEditMode(null)} style={{
        padding: "0.35em 0.9em", borderRadius: "6px",
        border: "1px solid #90cdf4", background: "#fff",
        color: "#2c5282", fontSize: "0.8rem", fontWeight: "600",
        cursor: "pointer", whiteSpace: "nowrap",
      }}>
        ← Vorschau
      </button>
    </div>
  );

  const SaveButton = () => (
    <button onClick={handleSave} disabled={saving} style={{
      padding: "0.65em 2em", borderRadius: "7px", border: "none",
      background: saving ? "#a0aec0" : "#4a2c2a",
      color: "#fff", fontWeight: "700", fontSize: "0.95rem",
      cursor: saving ? "not-allowed" : "pointer",
    }}>
      {saving ? "Wird gespeichert…" : "Änderungen speichern"}
    </button>
  );

  // ── Review edit mode ───────────────────────────────────────────────────────
  if (editMode === "review") {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SubTabs />
        <EditBanner />
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Seitentext bearbeiten
          </h3>

          <label style={labelStyle}>Seitentitel</label>
          <input style={inputStyle} value={reviewForm.pageTitle}
            onChange={(e) => setReviewField("pageTitle", e.target.value)} />

          <label style={labelStyle}>Freigabe-Text (Haupttext)</label>
          <textarea style={{ ...taStyle, minHeight: "90px" }}
            value={reviewForm.approveMainText}
            onChange={(e) => setReviewField("approveMainText", e.target.value)} />

          <label style={labelStyle}>Freigabe-Hinweise (ein Punkt pro Zeile)</label>
          <textarea style={{ ...taStyle, minHeight: "120px" }}
            value={(reviewForm.approveBullets || []).join("\n")}
            onChange={(e) => setReviewField("approveBullets", e.target.value.split("\n"))} />

          <label style={labelStyle}>Anpassungswunsch — Bezeichnung</label>
          <input style={inputStyle} value={reviewForm.modificationLabel}
            onChange={(e) => setReviewField("modificationLabel", e.target.value)} />

          <label style={labelStyle}>Anpassungswunsch — Hinweistext</label>
          <textarea style={{ ...taStyle, minHeight: "80px" }}
            value={reviewForm.modificationSubtext}
            onChange={(e) => setReviewField("modificationSubtext", e.target.value)} />

          <label style={labelStyle}>Textfeld-Bezeichnung</label>
          <input style={inputStyle} value={reviewForm.feedbackLabel}
            onChange={(e) => setReviewField("feedbackLabel", e.target.value)} />

          <label style={labelStyle}>Textfeld-Platzhalter</label>
          <input style={inputStyle} value={reviewForm.feedbackPlaceholder}
            onChange={(e) => setReviewField("feedbackPlaceholder", e.target.value)} />

          <label style={labelStyle}>Beschriftung Senden-Button</label>
          <input style={{ ...inputStyle, marginBottom: "1.75em" }}
            value={reviewForm.submitButtonText}
            onChange={(e) => setReviewField("submitButtonText", e.target.value)} />

          <SaveButton />
        </div>
      </div>
    );
  }

  // ── Thank-you edit mode ────────────────────────────────────────────────────
  if (editMode === "thankyou") {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <SubTabs />
        <EditBanner />
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Danke-Seite bearbeiten
          </h3>

          <label style={labelStyle}>Überschrift</label>
          <input style={inputStyle} value={thankYouForm.headline}
            onChange={(e) => setThankYouField("headline", e.target.value)} />

          <label style={labelStyle}>Einleitungstext</label>
          <textarea style={{ ...taStyle, minHeight: "70px" }}
            value={thankYouForm.subtext}
            onChange={(e) => setThankYouField("subtext", e.target.value)} />

          <label style={labelStyle}>Abschnittstitel "Wie geht es weiter?"</label>
          <input style={inputStyle} value={thankYouForm.nextStepsTitle}
            onChange={(e) => setThankYouField("nextStepsTitle", e.target.value)} />

          <label style={labelStyle}>Schritte (ein Punkt pro Zeile)</label>
          <textarea style={{ ...taStyle, minHeight: "130px" }}
            value={(thankYouForm.nextStepsBullets || []).join("\n")}
            onChange={(e) =>
              setThankYouField("nextStepsBullets", e.target.value.split("\n"))} />

          <label style={labelStyle}>Fußnote</label>
          <textarea style={{ ...taStyle, minHeight: "60px", marginBottom: "1.75em" }}
            value={thankYouForm.footerNote}
            onChange={(e) => setThankYouField("footerNote", e.target.value)} />

          <SaveButton />
        </div>
      </div>
    );
  }

  // ── Thank-you sub-tab (preview) ────────────────────────────────────────────
  if (subTab === "thankyou") {
    return (
      <div>
        <SubTabs />
        <div style={{
          maxWidth: 560, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "1em", marginBottom: "0.75em",
        }}>
          <div style={{
            flex: 1, background: "#fffbeb", border: "1px solid #f6e05e",
            borderRadius: "8px", padding: "0.6em 1em",
            color: "#744210", fontSize: "0.8rem", fontWeight: "500",
          }}>
            Vorschau-Modus — so sehen deine Kunden die Danke-Seite.
            {saved && (
              <span style={{ marginLeft: "0.75em", color: "#276749", fontWeight: "700" }}>
                ✓ Gespeichert!
              </span>
            )}
          </div>
          <button onClick={() => setEditMode("thankyou")} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #cbd5e0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
            cursor: "pointer", whiteSpace: "nowrap",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
        <ThankYouPage textConfig={thankYouForm} />
      </div>
    );
  }

  // ── Review sub-tab (preview) ───────────────────────────────────────────────
  return (
    <div>
      <SubTabs />
      <div style={{
        maxWidth: 600, margin: "0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "1em", marginBottom: "0.75em",
      }}>
        <div style={{
          flex: 1, background: "#fffbeb", border: "1px solid #f6e05e",
          borderRadius: "8px", padding: "0.6em 1em",
          color: "#744210", fontSize: "0.8rem", fontWeight: "500",
        }}>
          Vorschau-Modus — so sehen deine Kunden die Seite.
          {saved && (
            <span style={{ marginLeft: "0.75em", color: "#276749", fontWeight: "700" }}>
              ✓ Gespeichert!
            </span>
          )}
        </div>
        <button onClick={() => setEditMode("review")} style={{
          padding: "0.45em 1.1em", borderRadius: "6px",
          border: "1px solid #cbd5e0", background: "#fff",
          color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
          cursor: "pointer", whiteSpace: "nowrap",
        }}>
          ✏️ Text bearbeiten
        </button>
      </div>
      <DraftReviewForm previewMode sampleData={SAMPLE} textConfig={reviewForm} />
    </div>
  );
};

export default DraftReviewPreview;
